import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

function EditScreeningQuestions() {
  const navigate = useNavigate();
  const { requirementId } = useParams();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [approving, setApproving] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  
  // Data states
  const [meetingTitle, setMeetingTitle] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [screeningQuestions, setScreeningQuestions] = useState('');
  const [originalQuestions, setOriginalQuestions] = useState('');
  const [isApproved, setIsApproved] = useState(false);
  const [meetingId, setMeetingId] = useState(null);

  const fetchRequirementData = useCallback(async (token) => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/api/requirements/${requirementId}/view`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const result = await response.json();
        const data = result.data;
        
        setJobTitle(data.job_title || '');
        setMeetingTitle(data.meeting_title || data.job_title || '');
        setScreeningQuestions(data.screening_questions || '');
        setOriginalQuestions(data.screening_questions || '');
        setIsApproved(data.approved_by_hiring_manager || false);
        setMeetingId(data.meeting_id || null);
      } else {
        setError('Failed to load requirement data');
      }
    } catch (error) {
      console.error('Error fetching requirement:', error);
      setError('Connection error while loading data');
    } finally {
      setLoading(false);
    }
  }, [requirementId]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    
    if (!token) {
      navigate('/');
      return;
    }

    try {
      // Fetch requirement data
      fetchRequirementData(token);
    } catch (error) {
      console.error('Invalid token:', error);
      navigate('/');
    }
  }, [requirementId, navigate, fetchRequirementData]);

  const handleSaveDraft = async () => {
    if (isApproved) {
      setError('Cannot edit approved screening questions');
      return;
    }

    setSaving(true);
    setError('');
    setSuccessMessage('');

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/requirements/${requirementId}/update-screening`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          screening_questions: screeningQuestions
        })
      });

      if (response.ok) {
        setOriginalQuestions(screeningQuestions);
        setSuccessMessage('Draft saved successfully!');
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setError('Failed to save draft');
      }
    } catch (error) {
      console.error('Error saving draft:', error);
      setError('Connection error while saving');
    } finally {
      setSaving(false);
    }
  };

  const handleApprove = async () => {
    if (isApproved) {
      setError('Screening questions already approved');
      return;
    }

    // Confirm dialog
    if (!window.confirm('Are you sure you want to approve these screening questions? This action cannot be undone.')) {
      return;
    }

    setApproving(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/requirements/${requirementId}/approve`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          screening_questions: screeningQuestions,
          meeting_id: meetingId
        })
      });

      if (response.ok) {
        setSuccessMessage('Screening questions approved successfully!');
        setIsApproved(true);
        
        // Redirect back to dashboard after 2 seconds with refresh flag
        setTimeout(() => {
          navigate('/dashboard/user', { state: { refreshMeetings: true } });
        }, 2000);
      } else {
        const result = await response.json();
        setError(result.detail || 'Failed to approve');
      }
    } catch (error) {
      console.error('Error approving:', error);
      setError('Connection error while approving');
    } finally {
      setApproving(false);
    }
  };

  const handleGoBack = () => {
    if (screeningQuestions !== originalQuestions && !isApproved) {
      if (window.confirm('You have unsaved changes. Are you sure you want to go back?')) {
        navigate('/dashboard/user');
      }
    } else {
      navigate('/dashboard/user');
    }
  };

  const hasChanges = screeningQuestions !== originalQuestions;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={handleGoBack}
                className="flex items-center gap-2 text-gray-600 hover:text-brand-blue transition-colors font-medium"
              >
                <span className="text-xl">←</span> Back to Dashboard
              </button>
              <div className="h-8 w-px bg-gray-300"></div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Edit Screening Questions</h1>
                <p className="text-sm text-gray-600 mt-0.5">{meetingTitle || jobTitle}</p>
              </div>
            </div>

            {/* Approval Status Badge */}
            {isApproved && (
              <span className="px-6 py-2 rounded-full text-sm font-bold bg-gradient-to-r from-green-500 to-green-600 text-white shadow-lg">
                ✓ APPROVED
              </span>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto p-6">
        {/* Error Message */}
        {error && (
          <div className="mb-6 bg-red-50 border-l-4 border-red-400 text-red-700 px-6 py-4 rounded-lg shadow-sm">
            <div className="flex items-center gap-2">
              <span className="text-xl">⚠️</span>
              <span className="font-semibold">{error}</span>
            </div>
          </div>
        )}

        {/* Success Message */}
        {successMessage && (
          <div className="mb-6 bg-green-50 border-l-4 border-green-400 text-green-700 px-6 py-4 rounded-lg shadow-sm">
            <div className="flex items-center gap-2">
              <span className="text-xl">✓</span>
              <span className="font-semibold">{successMessage}</span>
            </div>
          </div>
        )}

        {loading ? (
          <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-20 text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-4 border-brand-blue border-t-transparent mx-auto"></div>
            <p className="text-gray-600 mt-6 text-lg">Loading screening questions...</p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Editor Card */}
            <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
              <div className="bg-gradient-to-r from-brand-blue to-blue-600 px-6 py-4">
                <h2 className="text-lg font-bold text-white flex items-center gap-2">
                  <span className="text-2xl">📋</span>
                  Screening Questions
                </h2>
                <p className="text-blue-100 text-sm mt-1">Edit the questions below and save your changes</p>
              </div>

              <div className="p-6">
                <textarea
                  value={screeningQuestions}
                  onChange={(e) => setScreeningQuestions(e.target.value)}
                  disabled={isApproved}
                  className={`w-full h-[500px] p-6 border-2 rounded-lg font-mono text-sm leading-relaxed resize-none focus:outline-none focus:ring-4 ${
                    isApproved
                      ? 'bg-gray-50 border-gray-300 text-gray-600 cursor-not-allowed'
                      : 'bg-white border-gray-300 text-gray-900 focus:border-brand-blue focus:ring-blue-100'
                  }`}
                  placeholder="Enter screening questions here..."
                />

                {isApproved && (
                  <div className="mt-4 bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
                    <span className="text-2xl">🔒</span>
                    <div>
                      <p className="text-green-800 font-semibold">Questions Approved</p>
                      <p className="text-green-600 text-sm">These screening questions have been approved and cannot be edited.</p>
                    </div>
                  </div>
                )}

                {hasChanges && !isApproved && (
                  <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-center gap-3">
                    <span className="text-2xl">⚠️</span>
                    <div>
                      <p className="text-yellow-800 font-semibold">Unsaved Changes</p>
                      <p className="text-yellow-600 text-sm">You have unsaved changes. Click "Save Draft" to save your edits.</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            {!isApproved && (
              <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
                <div className="flex items-center justify-between gap-4">
                  <div className="text-sm text-gray-600">
                    <p className="font-semibold">Ready to approve?</p>
                    <p className="text-xs mt-1">Save your draft first, then approve when ready. Approval is final.</p>
                  </div>

                  <div className="flex items-center gap-4">
                    {/* Save Draft Button */}
                    <button
                      onClick={handleSaveDraft}
                      disabled={saving || !hasChanges}
                      className={`px-8 py-3 rounded-xl font-bold transition-all shadow-lg ${
                        saving || !hasChanges
                          ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                          : 'bg-gradient-to-r from-blue-500 to-blue-600 text-white hover:from-blue-600 hover:to-blue-700 transform hover:scale-105'
                      }`}
                    >
                      {saving ? (
                        <span className="flex items-center gap-2">
                          <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                          Saving...
                        </span>
                      ) : (
                        <span className="flex items-center gap-2">
                          <span>💾</span> Save Draft
                        </span>
                      )}
                    </button>

                    {/* Approve Button */}
                    <button
                      onClick={handleApprove}
                      disabled={approving || hasChanges}
                      className={`px-8 py-3 rounded-xl font-bold transition-all shadow-lg ${
                        approving || hasChanges
                          ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                          : 'bg-gradient-to-r from-green-500 to-green-600 text-white hover:from-green-600 hover:to-green-700 transform hover:scale-105'
                      }`}
                      title={hasChanges ? 'Save draft before approving' : 'Approve screening questions'}
                    >
                      {approving ? (
                        <span className="flex items-center gap-2">
                          <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                          Approving...
                        </span>
                      ) : (
                        <span className="flex items-center gap-2">
                          <span>✓</span> Approve & Submit
                        </span>
                      )}
                    </button>
                  </div>
                </div>

                {hasChanges && (
                  <div className="mt-4 text-center text-xs text-yellow-600 font-semibold">
                    ⚠️ Please save your draft before approving
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}

export default EditScreeningQuestions;